import React, { useState, useEffect, useCallback } from 'react';
import { getVideos, getCategories, createVideo, updateVideo, deleteVideo, toggleLock } from '../Videoapi/Videoapi';
import './Videoapi.css';

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');
  const [modal, setModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ title: '', url: '', category: '', thumbnail: '', isLocked: false, unlockCost: 499 });
  const LIMIT = 15;

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search) params.search = search;
      if (filterCat !== 'All') params.category = filterCat;
      const res = await getVideos(params);
      setVideos(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch { showToast('Failed to load videos', 'error'); }
    finally { setLoading(false); }
  }, [page, search, filterCat]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);
  useEffect(() => { getCategories().then(r => setCategories(r.data.data || [])); }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openCreate = () => {
    setEditItem(null);
    setForm({ title: '', url: '', category: categories[0]?.name || '', thumbnail: '', isLocked: false, unlockCost: 499 });
    setModal('form');
  };

  const openEdit = (v) => {
    setEditItem(v);
    setForm({ title: v.title, url: v.url, category: v.category, thumbnail: v.thumbnail || '', isLocked: v.isLocked, unlockCost: v.unlockCost || 499 });
    setModal('form');
  };

  const handleSubmit = async () => {
    if (!form.title || !form.url || !form.category) return showToast('Title, URL and Category are required', 'error');
    setSaving(true);
    try {
      if (editItem) { await updateVideo(editItem._id, form); showToast('Video updated!'); }
      else { await createVideo(form); showToast('Video created!'); }
      setModal(null);
      fetchVideos();
    } catch (err) { showToast(err.response?.data?.message || 'Error', 'error'); }
    finally { setSaving(false); }
  };

  const handleToggleLock = async (v) => {
    try {
      await toggleLock(v._id, !v.isLocked, v.unlockCost);
      showToast(`Video ${!v.isLocked ? 'locked' : 'unlocked'}`);
      fetchVideos();
    } catch { showToast('Failed', 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteVideo(id);
      showToast('Video deleted');
      setConfirmDelete(null);
      fetchVideos();
    } catch { showToast('Failed to delete', 'error'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="vid-root">
      <div className="vid-header">
        <div>
          <h1 className="vid-header-title">🎬 Videos Management</h1>
          <p className="vid-header-sub">{total} total videos</p>
        </div>
        <div className="vid-header-right">
          <input className="search-box" placeholder="🔍 Search videos..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select className="filter-select" value={filterCat}
            onChange={e => { setFilterCat(e.target.value); setPage(1); }}>
            <option value="All">All Categories</option>
            {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
          </select>
          <button className="btn-primary" onClick={openCreate}>＋ Add Video</button>
        </div>
      </div>

      <div className="vid-content">
        <div className="vid-table-wrap">
          {loading ? (
            <div className="loading-spinner"><div className="spinner" /></div>
          ) : videos.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">🎥</div><p>No videos found.</p></div>
          ) : (
            <>
              <table className="vid-table">
                <thead>
                  <tr>
                    <th>#</th><th>Video</th><th>Category</th><th>Status</th><th>Lock</th><th>Cost</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {videos.map((v, i) => (
                    <tr key={v._id}>
                      <td>{(page - 1) * LIMIT + i + 1}</td>
                      <td>
                        <div className="vid-title-cell">
                          <div className="thumb-wrap">
                            {v.thumbnail ? <img src={v.thumbnail} alt="" onError={e => e.target.style.display = 'none'} /> : '▶'}
                          </div>
                          <span className="vid-title-text" title={v.title}>{v.title}</span>
                        </div>
                      </td>
                      <td><span className="badge badge-sky">{v.category}</span></td>
                      <td><span className={`badge ${v.isActive ? 'badge-green' : 'badge-red'}`}>{v.isActive ? 'Active' : 'Inactive'}</span></td>
                      <td><span className={`badge ${v.isLocked ? 'badge-red' : 'badge-green'}`}>{v.isLocked ? '🔒 Locked' : '🔓 Free'}</span></td>
                      <td className="cost-cell">₹{v.unlockCost || 499}</td>
                      <td>
                        <div className="actions-cell">
                          <button className="btn-sm btn-sm-edit" onClick={() => openEdit(v)}>✏️ Edit</button>
                          <button className={`btn-sm ${v.isLocked ? 'btn-sm-unlock' : 'btn-sm-lock'}`} onClick={() => handleToggleLock(v)}>
                            {v.isLocked ? '🔓' : '🔒'}
                          </button>
                          <button className="btn-sm btn-sm-del" onClick={() => setConfirmDelete(v)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="pagination">
                  <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                    <button key={i + 1} className={`page-btn ${page === i + 1 ? 'active' : ''}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
                  ))}
                  <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {modal === 'form' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal">
            <h2 className="modal-title">{editItem ? '✏️ Edit Video' : '➕ Add Video'}</h2>
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input className="form-input" placeholder="Video title" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">YouTube URL *</label>
              <input className="form-input" placeholder="https://youtube.com/watch?v=..." value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select className="form-input" value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Unlock Cost (₹)</label>
                <input className="form-input" type="number" value={form.unlockCost}
                  onChange={e => setForm(f => ({ ...f, unlockCost: Number(e.target.value) }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Thumbnail URL (optional)</label>
              <input className="form-input" placeholder="https://..." value={form.thumbnail}
                onChange={e => setForm(f => ({ ...f, thumbnail: e.target.value }))} />
              {form.thumbnail && (
                <img src={form.thumbnail} alt="thumb"
                  style={{ marginTop: 8, height: 56, borderRadius: 6, objectFit: 'cover', border: '1px solid #e2e8f0' }}
                  onError={e => e.target.style.display = 'none'} />
              )}
            </div>
            <div className="toggle-row">
              <span className="toggle-label">🔒 Lock this video</span>
              <button className={`toggle ${form.isLocked ? 'on' : ''}`}
                onClick={() => setForm(f => ({ ...f, isLocked: !f.isLocked }))} />
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary-dark" onClick={handleSubmit} disabled={saving}>
                {saving ? '⏳ Saving...' : editItem ? '✅ Update' : '✅ Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon">🗑️</div>
            <h3 className="confirm-title">Delete Video?</h3>
            <p className="confirm-text">Delete <strong>{confirmDelete.title}</strong>? This cannot be undone.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDelete(confirmDelete._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.type === 'success' ? '✅' : '❌'} {toast.msg}</div>}
    </div>
  );
}